import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { useNavigate } from 'react-router-dom';
import { Api } from '../../services/api/axiosApi';

// Certificado digital da empresa (GET /empresa/certificado, backend):
//  - login: se faltam 15 dias ou menos (ou já venceu), mostra um aviso em
//    tela — a cada login, não a cada F5 (ver marcarLoginParaAvisoCertificado,
//    chamado pela tela de login);
//  - vencido (ou ilegível: senha errada/arquivo inválido): ícone de alerta ao
//    lado do avatar; clicar explica o que está errado e o que fazer.

export type TSituacaoCertificado = 'ok' | 'vencendo' | 'vencido' | 'sem_certificado' | 'erro';

export interface IStatusCertificado {
  situacao: TSituacaoCertificado;
  mensagem: string;
  validoAte?: string;
  diasRestantes?: number;
  titular?: string;
}

const CHAVE_LOGIN = 'SIAC_AVISO_CERTIFICADO_LOGIN';

// Chamado pela tela de login logo depois de entrar: o próximo carregamento
// do sistema mostra o aviso (se for o caso) uma vez e apaga a marca.
export const marcarLoginParaAvisoCertificado = () => {
  try { sessionStorage.setItem(CHAVE_LOGIN, '1'); } catch { /* sem sessionStorage: só não avisa */ }
};

// Uma consulta compartilhada por todas as telas (avatar no computador, no
// celular e o aviso do login) — e de novo a cada 6 horas com o sistema aberto.
let status: IStatusCertificado | null = null;
let consulta: Promise<IStatusCertificado | null> | null = null;
let consultadoEm = 0;
const ouvintes = new Set<(s: IStatusCertificado | null) => void>();
const SEIS_HORAS = 6 * 60 * 60 * 1000;

const consultarStatus = (): Promise<IStatusCertificado | null> => {
  if (consulta && Date.now() - consultadoEm < SEIS_HORAS) return consulta;
  consultadoEm = Date.now();
  consulta = Api.get<IStatusCertificado>('/empresa/certificado')
    .then(({ data }) => data)
    // Backend sem a rota (versão antiga) ou sem conexão: não mostra nada.
    .catch(() => null)
    .then((resultado) => {
      status = resultado;
      ouvintes.forEach((ouvinte) => ouvinte(status));
      return status;
    });
  return consulta;
};

// Força uma nova consulta (ex.: logo depois de trocar o certificado na tela
// de Configurações) — avatar, aviso e a própria tela se atualizam juntos.
export const atualizarStatusCertificado = (novo?: IStatusCertificado | null) => {
  if (novo) {
    status = novo;
    consultadoEm = Date.now();
    consulta = Promise.resolve(novo);
    ouvintes.forEach((ouvinte) => ouvinte(status));
    return consulta;
  }
  consultadoEm = 0;
  consulta = null;
  return consultarStatus();
};

export const useStatusCertificado = () => {
  const [atual, setAtual] = useState<IStatusCertificado | null>(status);
  useEffect(() => {
    ouvintes.add(setAtual);
    consultarStatus();
    const intervalo = setInterval(consultarStatus, SEIS_HORAS);
    return () => { ouvintes.delete(setAtual); clearInterval(intervalo); };
  }, []);
  return atual;
};

const dataBR = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');

// ---------- Diálogo explicativo ----------

interface IDialogCertificadoProps {
  open: boolean;
  status: IStatusCertificado | null;
  onClose: () => void;
}

export const DialogCertificado: React.FC<IDialogCertificadoProps> = ({ open, status: s, onClose }) => {
  const navigate = useNavigate();
  if (!s) return null;
  const vencido = s.situacao === 'vencido';
  const problema = s.situacao === 'erro';
  const severidade = vencido || problema ? 'error' : s.situacao === 'vencendo' ? 'warning' : 'success';
  const titulo = vencido
    ? 'Certificado digital vencido'
    : problema
      ? 'Problema no certificado digital'
      : s.situacao === 'vencendo'
        ? 'Certificado digital prestes a vencer'
        : 'Certificado digital';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {severidade === 'error' ? <ErrorRoundedIcon color="error" /> : severidade === 'warning' ? <WarningAmberRoundedIcon color="warning" /> : <VerifiedUserRoundedIcon color="success" />}
        {titulo}
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity={severidade} sx={{ mb: 2 }}>{s.mensagem}</Alert>
        {(s.validoAte || s.titular) && (
          <Box display="grid" gridTemplateColumns="auto 1fr" columnGap={2} rowGap={0.5}>
            {s.titular && (<><Typography variant="body2" color="text.secondary">Titular</Typography><Typography variant="body2" fontWeight={600}>{s.titular}</Typography></>)}
            {s.validoAte && (<><Typography variant="body2" color="text.secondary">Válido até</Typography><Typography variant="body2" fontWeight={600}>{dataBR(s.validoAte)}</Typography></>)}
            {s.situacao === 'vencendo' && typeof s.diasRestantes === 'number' && (
              <><Typography variant="body2" color="text.secondary">Faltam</Typography><Typography variant="body2" fontWeight={700} color="warning.dark">{s.diasRestantes <= 0 ? 'vence hoje' : `${s.diasRestantes} dia${s.diasRestantes === 1 ? '' : 's'}`}</Typography></>
            )}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          O certificado digital (A1) é o arquivo que assina as notas fiscais. Depois de renovar com a certificadora,
          envie o novo arquivo <b>.pfx</b> e a senha em <b>Configurações da empresa</b>.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" onClick={() => { onClose(); navigate('/empresa'); }}>Ir para Configurações da empresa</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------- Aviso no login ----------

// Renderizado uma vez dentro do sistema (MenuLateral): logo depois de um
// login, se o certificado vence em até 15 dias (ou já venceu/está com
// problema), abre o aviso. F5 não repete — só um novo login.
export const AvisoCertificadoLogin: React.FC = () => {
  const s = useStatusCertificado();
  const [aberto, setAberto] = useState(false);
  useEffect(() => {
    if (!s) return;
    let veioDoLogin = false;
    try { veioDoLogin = sessionStorage.getItem(CHAVE_LOGIN) === '1'; } catch { /* idem */ }
    if (!veioDoLogin) return;
    try { sessionStorage.removeItem(CHAVE_LOGIN); } catch { /* idem */ }
    if (['vencendo', 'vencido', 'erro'].includes(s.situacao)) setAberto(true);
  }, [s]);
  return <DialogCertificado open={aberto} status={s} onClose={() => setAberto(false)} />;
};

// ---------- Ícone ao lado do avatar ----------

// Só aparece com o certificado vencido ou ilegível (senha errada/arquivo
// inválido) — situações em que a emissão de notas já parou. Clicar explica.
export const IconeAlertaCertificado: React.FC<{ tamanho?: number }> = ({ tamanho = 22 }) => {
  const s = useStatusCertificado();
  const [aberto, setAberto] = useState(false);
  if (!s || !['vencido', 'erro'].includes(s.situacao)) return null;
  const vencido = s.situacao === 'vencido';
  return (
    <>
      <Tooltip title={vencido ? `Certificado digital vencido${s.validoAte ? ` em ${dataBR(s.validoAte)}` : ''} — clique para ver` : 'Problema no certificado digital — clique para ver'}>
        <IconButton
          size="small"
          aria-label="Alerta do certificado digital"
          onClick={(e) => { e.stopPropagation(); setAberto(true); }}
          sx={{
            p: 0.25,
            color: 'error.dark',
            bgcolor: 'background.paper',
            boxShadow: '0 0 0 2px rgba(0,0,0,0.08)',
            '&:hover': { bgcolor: 'background.paper' },
            animation: 'siacPulsoCert 2s ease-in-out infinite',
            '@keyframes siacPulsoCert': { '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.12)' } },
          }}
        >
          <ErrorRoundedIcon sx={{ fontSize: tamanho }} />
        </IconButton>
      </Tooltip>
      <DialogCertificado open={aberto} status={s} onClose={() => setAberto(false)} />
    </>
  );
};
